import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, MagnifyingGlass } from 'shared/icons';
import {
    ALERT_TONES,
    ICMS_TYPE_DESCRIPTIONS,
    ICMS_TYPE_LABELS,
    ICMS_TYPE_TONES,
    ICMS_TYPES,
    LINK_STATUS_LABELS,
    LINK_STATUS_TONES,
    MATCH_SOURCE_LABELS,
    MATCHED_BY_LABELS,
    PARAM_FIELDS,
    PARAM_GROUP_LABELS,
    RULE_SCOPE_LABELS,
    RULE_SCOPES,
} from '../../constants/pricing';
import { useAuth } from '../../hooks/useAuth';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';
import { FormatBrl, FormatDecimal1, FormatMoney } from '../../utils/format';
import { Pill } from '../campaigns/campaignCardShared';
import { DASH } from '../marketIntelligence/intelligenceShared';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import StandardTable from '../ui/StandardTable';
import Textarea from '../ui/Textarea';
import { useToast } from '../ui/Toast';

/**
 * Primitivas da Precificação (custo fiscal por SKU): pílulas de tipo de
 * ICMS, alertas, breakdown do custo líquido, tabela de lotes, editor de
 * parâmetros e o modal de regra. Moram aqui para a lista, a página do SKU
 * e a aba de notas não reimplementarem nada (CLAUDE.md §4).
 */

export { DASH };

/** 'YYYY-MM-DD…' (date ou datetime ISO) → 'DD/MM/YYYY' por fatia de string (sem fuso). */
export function FormatDay(value) {
    const raw = String(value ?? '').slice(0, 10);
    const parts = raw.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : DASH;
}

/** Fração (0.18) → '18,00%'. */
export function FormatFraction(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return DASH;
    }
    return `${FormatBrl(n * 100)}%`;
}

export const Money = ({ value, className = '' }) => (
    <span className={`tabular-nums ${className}`.trim()}>{FormatMoney(value)}</span>
);

export const IcmsTypePill = ({ type, className = '' }) =>
    type ? (
        <Pill tone={ICMS_TYPE_TONES[type] || 'mono'} className={className} title={ICMS_TYPE_DESCRIPTIONS[type]}>
            {ICMS_TYPE_LABELS[type] || type}
        </Pill>
    ) : (
        <span className="text-mono-400">{DASH}</span>
    );

export const MatchedByLabel = ({ matchedBy }) => (
    <span className="text-xs text-mono-500 dark:text-mono-400">
        {MATCHED_BY_LABELS[matchedBy] || matchedBy || DASH}
    </span>
);

/** Alertas do motor/cadastro como badges; `hide` esconde códigos ruidosos na lista. */
export const AlertBadges = ({ alerts = [], hide = [], compact = false }) => {
    const visible = (Array.isArray(alerts) ? alerts : []).filter((alert) => !hide.includes(alert.code));
    if (visible.length === 0) {
        return <span className="text-mono-400">{DASH}</span>;
    }
    return (
        <div className="flex flex-wrap gap-1">
            {visible.map((alert) => (
                <Badge
                    key={alert.code}
                    variant={ALERT_TONES[alert.code] || 'default'}
                    className={compact ? 'whitespace-nowrap' : ''}
                >
                    <span title={alert.message}>
                        {compact ? alert.code.toLowerCase().replace(/_/g, ' ') : alert.message}
                    </span>
                </Badge>
            ))}
        </div>
    );
};

export const SkuLink = ({ sku, className = '' }) =>
    sku ? (
        <Link
            to={`/precificacao/sku/${encodeURIComponent(sku)}`}
            onClick={(event) => event.stopPropagation()}
            className={`rounded-md bg-mono-100 px-1.5 py-px font-mono text-xs text-mono-700 hover:underline dark:bg-mono-900 dark:text-mono-300 ${className}`.trim()}
        >
            {sku}
        </Link>
    ) : (
        <span className="text-mono-400">{DASH}</span>
    );

/**
 * Decomposição do custo líquido de UM lote (saída de ComputeNetCost):
 * totais do lote e por unidade, linha a linha, até CMV SP / fora / blend.
 */
/** Linha de dedução: sinal negativo sem produzir "-0,00" quando o crédito é zero. */
function negative(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const n = Number(value);
    return n > 0 ? -n : 0;
}

export const CostBreakdownTable = ({ cost }) => {
    if (!cost) {
        return <p className="text-sm italic text-mono-500">Sem custo calculado (lote sem SKU vinculado).</p>;
    }
    const unit = cost.per_unit || {};
    const isSt = cost.is_st;
    const rows = [
        { label: 'Mercadoria (vProd + frete/seguro/outros − desconto)', total: cost.mercadoria, unit: unit.mercadoria },
        { label: 'IPI (custo, sem crédito)', total: cost.ipi, unit: unit.ipi },
        ...(isSt
            ? [
                  {
                      label: `ST ${cost.st_estimado ? '(ESTIMADO pelo fallback)' : 'destacado/retido na nota'}`,
                      total: cost.st,
                      unit: unit.st,
                      tone: cost.st_estimado ? 'danger' : '',
                  },
              ]
            : []),
        { label: 'Custo bruto', total: cost.custo_bruto, unit: unit.custo_bruto, strong: true },
        {
            label: `(−) Crédito de ICMS (${FormatFraction(cost.aliq_icms_compra)} × mercadoria)`,
            total: negative(cost.credito_icms),
            unit: negative(unit.credito_icms),
        },
        {
            label: `(−) Crédito de PIS/COFINS (${FormatFraction(cost.params_used?.pis_cofins_pct)} × ${FormatMoney(cost.base_credito_pc)})`,
            total: negative(cost.credito_pc),
            unit: negative(unit.credito_pc),
        },
    ];
    return (
        <div className="overflow-x-auto rounded-xl border border-mono-200 dark:border-mono-800">
            <table className="w-full text-sm">
                <thead className="bg-mono-50 text-left text-[11px] uppercase tracking-wide text-mono-500 dark:bg-mono-900 dark:text-mono-400">
                    <tr>
                        <th className="px-3 py-2">Linha</th>
                        <th className="px-3 py-2 text-right">Lote ({FormatDecimal1(cost.qty)} un)</th>
                        <th className="px-3 py-2 text-right">Por unidade</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label} className="border-t border-mono-100 dark:border-mono-900">
                            <td
                                className={`px-3 py-1.5 ${row.strong ? 'font-semibold' : ''} ${row.tone === 'danger' ? 'text-danger-600 dark:text-danger-400' : ''}`}
                            >
                                {row.label}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">{FormatMoney(row.total)}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">{FormatMoney(row.unit)}</td>
                        </tr>
                    ))}
                    {isSt ? (
                        <>
                            <tr className="border-t border-mono-200 bg-mono-50/60 dark:border-mono-800 dark:bg-mono-900/40">
                                <td className="px-3 py-1.5 font-semibold">CMV líquido — venda em SP</td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
                                    {FormatMoney(cost.cmv_sp)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
                                    {FormatMoney(unit.cmv_sp)}
                                </td>
                            </tr>
                            <tr className="border-t border-mono-100 dark:border-mono-900">
                                <td className="px-3 py-1.5 font-semibold">
                                    CMV líquido — venda fora de SP
                                    <span className="ml-2 text-xs font-normal text-mono-500">
                                        recupera {FormatMoney(cost.recuperacao_esperada_fora)} de{' '}
                                        {FormatMoney(cost.recuperavel_fora)} (honorário{' '}
                                        {FormatFraction(cost.params_used?.honorario_recuperacao)})
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
                                    {FormatMoney(cost.cmv_fora)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
                                    {FormatMoney(unit.cmv_fora)}
                                </td>
                            </tr>
                        </>
                    ) : null}
                    <tr className="border-t-2 border-mono-300 bg-mono-100/70 dark:border-mono-700 dark:bg-mono-900">
                        <td className="px-3 py-2 font-bold">
                            CMV líquido{isSt ? ` (blend: ${FormatFraction(cost.params_used?.mix_sp)} SP)` : ''}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold tabular-nums">
                            {FormatMoney(cost.cmv_blend)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold tabular-nums">
                            {FormatMoney(unit.cmv_blend)}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

/** Situação do vínculo fornecedor → produto (de-para). */
export const LinkStatusPill = ({ status, matchSource = null, className = '' }) => (
    <Pill
        tone={LINK_STATUS_TONES[status] || 'mono'}
        className={className}
        title={matchSource ? MATCH_SOURCE_LABELS[matchSource] || matchSource : undefined}
    >
        {LINK_STATUS_LABELS[status] || status || DASH}
    </Pill>
);

/**
 * Seletor de produto do Bling (o lado "nosso" do de-para é SEMPRE um
 * products.sku). Busca no servidor com debounce; escolher chama onSelect
 * com o produto. Não aceita texto livre: sem produto no cadastro, sem vínculo.
 */
export const ProductPicker = ({ onSelect, placeholder = 'Buscar SKU, título ou EAN…', disabled = false }) => {
    const [term, setTerm] = useState('');
    const debounced = useDebounce(term, 300);
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        const query = debounced.trim();
        if (query.length < 2) {
            setResults([]);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        api.searchPricingProducts({ search: query, limit: 12 })
            .then((payload) => {
                if (!cancelled) {
                    setResults(Array.isArray(payload?.items) ? payload.items : []);
                    setOpen(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setResults([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [debounced]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (boxRef.current && !boxRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    return (
        <div ref={boxRef} className="relative w-64">
            <div className="flex items-center gap-1 rounded-md border border-mono-300 bg-white px-2 dark:border-mono-700 dark:bg-mono-950">
                <MagnifyingGlass size={14} className="shrink-0 text-mono-400" />
                <input
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    onClick={(event) => event.stopPropagation()}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="w-full bg-transparent py-1 text-xs text-black outline-none dark:text-white"
                />
            </div>
            {open ? (
                <ul className="absolute z-20 mt-1 max-h-64 w-80 overflow-auto rounded-lg border border-mono-200 bg-white p-1 shadow-lg dark:border-mono-700 dark:bg-mono-950">
                    {loading ? <li className="px-2 py-1 text-xs text-mono-500">Buscando…</li> : null}
                    {!loading && results.length === 0 ? (
                        <li className="px-2 py-1 text-xs text-mono-500">Nenhum produto do Bling com esse termo.</li>
                    ) : null}
                    {results.map((product) => (
                        <li key={product.sku}>
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setOpen(false);
                                    setTerm('');
                                    onSelect?.(product);
                                }}
                                className="flex w-full flex-col items-start rounded-md px-2 py-1 text-left hover:bg-mono-100 dark:hover:bg-mono-900"
                            >
                                <span className="font-mono text-xs text-black dark:text-white">{product.sku}</span>
                                <span className="truncate text-[11px] text-mono-500">
                                    {product.title || DASH}
                                    {product.ean ? ` · EAN ${product.ean}` : ''}
                                    {product.supplier_code ? ` · forn. ${product.supplier_code}` : ''}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

/**
 * Controle inline do vínculo de UM item de nota: escolhe o produto do Bling
 * no seletor (vincula e confirma o de-para) ou confirma a sugestão que o
 * importador fez. Mexe no de-para, então vale para todas as notas daquele
 * produto do fornecedor.
 */
export const SupplierLinkControl = ({ lot, onLinked }) => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const [saving, setSaving] = useState(false);
    if (!checkPermission('pricing:write')) {
        return lot.sku ? (
            <div className="space-y-0.5">
                <SkuLink sku={lot.sku} />
                <LinkStatusPill status={lot.link_status} matchSource={lot.link_match_source} />
            </div>
        ) : (
            <span className="text-xs italic text-mono-500">sem vínculo</span>
        );
    }
    const save = async (payload, message) => {
        setSaving(true);
        try {
            const updated = await api.updatePricingSupplierProduct(lot.supplier_product_id, payload);
            toast.success(message);
            onLinked?.(updated);
        } catch (error) {
            toast.error(error?.message || 'Não foi possível salvar o vínculo');
        } finally {
            setSaving(false);
        }
    };
    if (!lot.sku) {
        return (
            <ProductPicker
                disabled={saving}
                onSelect={(product) => save({ sku: product.sku }, `${lot.supplier_code} → ${product.sku} vinculado`)}
            />
        );
    }
    return (
        <div className="flex flex-wrap items-center gap-1">
            <SkuLink sku={lot.sku} />
            <LinkStatusPill status={lot.link_status} matchSource={lot.link_match_source} />
            {lot.link_status === 'sugerido' ? (
                <Button
                    size="sm"
                    variant="secondary"
                    disabled={saving}
                    title="Confirmar o vínculo sugerido"
                    onClick={(event) => {
                        event.stopPropagation();
                        save({ status: 'confirmado' }, `${lot.supplier_code} → ${lot.sku} confirmado`);
                    }}
                >
                    <Check size={14} />
                </Button>
            ) : null}
        </div>
    );
};

/**
 * Tabela de lotes (histórico custo × entrada × NF). Linha clicável abre o
 * breakdown completo; itens sem SKU mostram o campo de vínculo.
 */
export const LotsTable = ({ lots = [], onLotUpdated = null, showSku = true, exportFileName = 'lotes-custo' }) => {
    const [expandedId, setExpandedId] = useState(null);
    const columns = [
        {
            key: 'emitted_at',
            label: 'Emissão',
            getValue: (row) => String(row.emitted_at || '').slice(0, 10),
            render: (row) => <span className="font-mono text-xs">{FormatDay(row.emitted_at)}</span>,
        },
        {
            key: 'nfe',
            label: 'NF-e',
            getValue: (row) => `${row.nfe_number || ''} ${row.issuer_name || ''}`,
            render: (row) => (
                <div className="min-w-0">
                    <p className="font-mono text-xs text-black dark:text-white">
                        nº {row.nfe_number || DASH}
                        {row.nfe_series ? `/${row.nfe_series}` : ''} · item {row.item_number}
                    </p>
                    <p className="truncate text-xs text-mono-500" title={row.issuer_name || ''}>
                        {row.issuer_name || row.issuer_cnpj || DASH}
                        {row.issuer_uf ? ` · ${row.issuer_uf}` : ''}
                    </p>
                </div>
            ),
        },
        {
            key: 'description',
            label: 'Item da nota',
            getValue: (row) => `${row.description || ''} ${row.supplier_code || ''}`,
            render: (row) => (
                <div className="min-w-0 max-w-[260px]">
                    <p className="truncate text-sm" title={row.description || ''}>
                        {row.description || DASH}
                    </p>
                    <p className="font-mono text-[11px] text-mono-500">
                        {row.supplier_code || DASH}
                        {row.supplier_ean ? ` · EAN ${row.supplier_ean}` : ''}
                    </p>
                </div>
            ),
        },
        ...(showSku
            ? [
                  {
                      key: 'sku',
                      label: 'Produto (de-para)',
                      getValue: (row) => `${row.sku || ''} ${row.link_status || ''}`,
                      render: (row) => <SupplierLinkControl lot={row} onLinked={onLotUpdated} />,
                  },
              ]
            : []),
        {
            key: 'qty',
            label: 'Qtd (un)',
            align: 'right',
            getValue: (row) => Number(row.qty || 0),
            render: (row) => (
                <div className="text-right">
                    <span className="tabular-nums">{FormatDecimal1(row.qty)}</span>
                    {Number(row.conversion_factor) !== 1 ? (
                        <p
                            className="text-[11px] text-mono-500"
                            title="quantidade da nota × fator de conversão do de-para"
                        >
                            {FormatDecimal1(row.qty_nfe)} {row.unit || 'un'} × {FormatDecimal1(row.conversion_factor)}
                        </p>
                    ) : null}
                </div>
            ),
        },
        {
            key: 'mercadoria',
            label: 'Mercadoria',
            align: 'right',
            getValue: (row) => Number(row.mercadoria || 0),
            render: (row) => <Money value={row.mercadoria} />,
        },
        {
            key: 'ipi',
            label: 'IPI',
            align: 'right',
            getValue: (row) => Number(row.ipi || 0),
            render: (row) => <Money value={row.ipi} />,
        },
        {
            key: 'st',
            label: 'ST',
            align: 'right',
            getValue: (row) => Number(row.cost?.st ?? Math.max(row.st || 0, row.icms_st_ret || 0)),
            render: (row) => (
                <span
                    className={`tabular-nums ${row.cost?.st_estimado ? 'text-danger-600 dark:text-danger-400' : ''}`}
                    title={row.cost?.st_estimado ? 'ST estimado pelo fallback' : ''}
                >
                    {FormatMoney(row.cost?.st ?? Math.max(row.st || 0, row.icms_st_ret || 0))}
                    {row.cost?.st_estimado ? '*' : ''}
                </span>
            ),
        },
        {
            key: 'aliq',
            label: 'Alíq. ICMS',
            align: 'right',
            getValue: (row) => Number(row.aliq_icms_compra || 0),
            render: (row) => <span className="tabular-nums">{FormatFraction(row.aliq_icms_compra)}</span>,
        },
        {
            key: 'cst',
            label: 'CST / CFOP',
            getValue: (row) => `${row.cst || ''} ${row.cfop || ''}`,
            render: (row) => (
                <span className="font-mono text-xs">
                    {row.cst || DASH} / {row.cfop || DASH}
                </span>
            ),
        },
        {
            key: 'type',
            label: 'Tratamento',
            getValue: (row) => row.cost?.icms_type || row.icms_type_suggested || '',
            render: (row) =>
                row.cost ? (
                    <div className="space-y-0.5">
                        <IcmsTypePill type={row.cost.icms_type} />
                        <MatchedByLabel matchedBy={row.matched_by} />
                    </div>
                ) : (
                    <div className="space-y-0.5">
                        <IcmsTypePill type={row.icms_type_suggested} />
                        <span className="text-xs text-mono-500">sugerido pela nota</span>
                    </div>
                ),
        },
        {
            key: 'cmv_sp',
            label: 'CMV SP/un',
            align: 'right',
            getValue: (row) => row.cost?.per_unit?.cmv_sp ?? -Infinity,
            render: (row) => <Money value={row.cost?.per_unit?.cmv_sp} />,
        },
        {
            key: 'cmv_fora',
            label: 'CMV fora/un',
            align: 'right',
            getValue: (row) => row.cost?.per_unit?.cmv_fora ?? -Infinity,
            render: (row) => <Money value={row.cost?.per_unit?.cmv_fora} />,
        },
        {
            key: 'cmv_blend',
            label: 'CMV mix/un',
            align: 'right',
            getValue: (row) => row.cost?.per_unit?.cmv_blend ?? -Infinity,
            render: (row) => <Money value={row.cost?.per_unit?.cmv_blend} className="font-semibold" />,
        },
        {
            key: 'alerts',
            label: 'Alertas',
            sortable: false,
            getValue: (row) => (row.cost?.alerts || []).map((alert) => alert.code).join(' '),
            render: (row) => <AlertBadges alerts={row.cost?.alerts || []} compact />,
        },
    ];
    return (
        <StandardTable
            rows={lots}
            columns={columns}
            rowKey={(row) => row.id}
            countLabel="lotes"
            emptyMessage="Nenhum lote — importe a NF-e de compra na aba Notas de entrada."
            exportFileName={exportFileName}
            searchEnabled={false}
            defaultPageSize={30}
            onRowClick={(row) => setExpandedId((prev) => (prev === row.id ? null : row.id))}
            rowClassName={(row) => (expandedId === row.id ? 'bg-mono-50 dark:bg-mono-900/60' : '')}
            renderExpandedRow={(row) =>
                expandedId === row.id ? (
                    <div className="space-y-2 p-3">
                        <p className="text-xs text-mono-500">
                            Chave {row.nfe_key} · NCM {row.ncm || DASH} · origem {row.orig ?? DASH} · ICMS próprio{' '}
                            {FormatMoney(row.icms_proprio)}
                            {row.icms_substituto ? ` · ICMS substituto ${FormatMoney(row.icms_substituto)}` : ''}
                            {row.icms_deson ? ` · ICMS desonerado ${FormatMoney(row.icms_deson)}` : ''}
                            {row.rule ? ` · regra "${row.rule.name}"` : ''}
                        </p>
                        <CostBreakdownTable cost={row.cost} />
                    </div>
                ) : null
            }
        />
    );
};

/**
 * Editor de `params`. `mode='rule'`: campo vazio = herda (só chaves
 * preenchidas viajam). `mode='company'`: prefill com o efetivo. Frações são
 * digitadas em % e convertidas na saída.
 */
export const ParamsEditor = ({ value = {}, onChange, mode = 'rule', groups = ['fiscal', 'st', 'simulador'] }) => {
    const setField = (key, next) => {
        const draft = { ...value };
        if (next === '' || next === null || next === undefined) {
            delete draft[key];
        } else {
            draft[key] = next;
        }
        onChange(draft);
    };
    return (
        <div className="space-y-4">
            {groups.map((group) => (
                <fieldset key={group} className="space-y-3">
                    <legend className="text-xs font-semibold uppercase tracking-wide text-mono-500">
                        {PARAM_GROUP_LABELS[group]}
                    </legend>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {PARAM_FIELDS.filter((field) => field.group === group).map((field) => {
                            const current = value[field.key];
                            if (field.kind === 'flag') {
                                return (
                                    <div key={field.key}>
                                        <Select
                                            label={field.label}
                                            value={current === undefined ? '' : String(current)}
                                            onChange={(event) =>
                                                setField(
                                                    field.key,
                                                    event.target.value === '' ? '' : event.target.value === 'true',
                                                )
                                            }
                                        >
                                            {mode === 'rule' ? <option value="">Herdar da empresa</option> : null}
                                            <option value="true">Sim</option>
                                            <option value="false">Não</option>
                                        </Select>
                                        <p className="mt-1 text-[11px] text-mono-500">{field.help}</p>
                                    </div>
                                );
                            }
                            if (field.kind === 'icms_type') {
                                return (
                                    <div key={field.key}>
                                        <Select
                                            label={field.label}
                                            value={current ?? ''}
                                            onChange={(event) => setField(field.key, event.target.value)}
                                        >
                                            {mode === 'rule' ? <option value="">Herdar da empresa</option> : null}
                                            {ICMS_TYPES.map((type) => (
                                                <option key={type} value={type}>
                                                    {ICMS_TYPE_LABELS[type]}
                                                </option>
                                            ))}
                                        </Select>
                                        <p className="mt-1 text-[11px] text-mono-500">{field.help}</p>
                                    </div>
                                );
                            }
                            return (
                                <div key={field.key}>
                                    <Input
                                        label={`${field.label} (%)`}
                                        type="number"
                                        inputMode="decimal"
                                        step="any"
                                        min={0}
                                        max={100}
                                        value={
                                            current === undefined || current === null
                                                ? ''
                                                : String(Math.round(current * 1e6) / 1e4)
                                        }
                                        placeholder={mode === 'rule' ? 'herdar' : ''}
                                        onChange={(event) => {
                                            const raw = event.target.value;
                                            if (raw === '') {
                                                setField(field.key, '');
                                                return;
                                            }
                                            const parsed = Number(raw);
                                            setField(
                                                field.key,
                                                Number.isFinite(parsed) ? Math.round(parsed * 1e4) / 1e6 : '',
                                            );
                                        }}
                                        className="font-mono"
                                    />
                                    <p className="mt-1 text-[11px] text-mono-500">{field.help}</p>
                                </div>
                            );
                        })}
                    </div>
                </fieldset>
            ))}
        </div>
    );
};

const EMPTY_RULE = {
    name: '',
    scope_type: 'sku',
    scope_value: '',
    icms_type: 'normal',
    priority: 0,
    active: true,
    valid_from: '',
    valid_to: '',
    note: '',
    params: {},
};

/** Modal de criação/edição de regra. `initial` (regra existente) ou `preset` (valores iniciais para nova). */
export const RuleFormModal = ({ isOpen, onClose, initial = null, preset = null, onSaved }) => {
    const toast = useToast();
    const [form, setForm] = useState(EMPTY_RULE);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const base = initial || { ...EMPTY_RULE, ...(preset || {}) };
        setForm({
            ...EMPTY_RULE,
            ...base,
            scope_value: base.scope_value || '',
            valid_from: base.valid_from || '',
            valid_to: base.valid_to || '',
            note: base.note || '',
            params: base.params || {},
        });
        setError(null);
    }, [isOpen, initial, preset]);

    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        const payload = {
            name: form.name.trim(),
            scope_type: form.scope_type,
            scope_value: form.scope_type === 'default' ? null : form.scope_value.trim(),
            icms_type: form.icms_type,
            priority: Number(form.priority) || 0,
            active: Boolean(form.active),
            valid_from: form.valid_from || null,
            valid_to: form.valid_to || null,
            note: form.note.trim() || null,
            params: form.params || {},
        };
        try {
            const saved = initial?.id
                ? await api.updatePricingRule(initial.id, payload)
                : await api.createPricingRule(payload);
            toast.success(initial?.id ? 'Regra atualizada' : 'Regra criada');
            onSaved?.(saved);
            onClose();
        } catch (err) {
            setError(err?.message || 'Não foi possível salvar a regra');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={initial?.id ? `Editar regra — ${initial.name}` : 'Nova regra de ICMS'}
            size="2xl"
        >
            <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        label="Nome"
                        value={form.name}
                        onChange={(event) => setField('name', event.target.value)}
                        disabled={saving}
                        autoComplete="off"
                    />
                    <Select
                        label="Tratamento de ICMS"
                        value={form.icms_type}
                        onChange={(event) => setField('icms_type', event.target.value)}
                        disabled={saving}
                    >
                        {ICMS_TYPES.map((type) => (
                            <option key={type} value={type}>
                                {ICMS_TYPE_LABELS[type]}
                            </option>
                        ))}
                    </Select>
                </div>
                <p className="rounded-lg bg-mono-50 p-3 text-xs text-mono-600 dark:bg-mono-900 dark:text-mono-300">
                    {ICMS_TYPE_DESCRIPTIONS[form.icms_type]}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Select
                        label="Escopo"
                        value={form.scope_type}
                        onChange={(event) => setField('scope_type', event.target.value)}
                        disabled={saving}
                    >
                        {RULE_SCOPES.map((scope) => (
                            <option key={scope} value={scope}>
                                {RULE_SCOPE_LABELS[scope]}
                            </option>
                        ))}
                    </Select>
                    <Input
                        label={form.scope_type === 'ncm' ? 'NCM (prefixo, 2 a 8 dígitos)' : 'SKU'}
                        value={form.scope_value}
                        onChange={(event) => setField('scope_value', event.target.value)}
                        disabled={saving || form.scope_type === 'default'}
                        placeholder={form.scope_type === 'default' ? 'vale para todos' : ''}
                        className="font-mono"
                        autoComplete="off"
                    />
                    <Input
                        label="Prioridade (desempate)"
                        type="number"
                        value={form.priority}
                        onChange={(event) => setField('priority', event.target.value)}
                        disabled={saving}
                        className="font-mono"
                    />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        label="Vigente a partir de"
                        type="date"
                        value={form.valid_from}
                        onChange={(event) => setField('valid_from', event.target.value)}
                        disabled={saving}
                    />
                    <Input
                        label="Vigente até"
                        type="date"
                        value={form.valid_to}
                        onChange={(event) => setField('valid_to', event.target.value)}
                        disabled={saving}
                    />
                    <Select
                        label="Ativa"
                        value={String(form.active)}
                        onChange={(event) => setField('active', event.target.value === 'true')}
                        disabled={saving}
                    >
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                    </Select>
                </div>
                <Textarea
                    label="Observação"
                    rows={2}
                    value={form.note}
                    onChange={(event) => setField('note', event.target.value)}
                    disabled={saving}
                />
                <details
                    className="rounded-lg border border-mono-200 p-3 dark:border-mono-800"
                    open={Object.keys(form.params || {}).length > 0}
                >
                    <summary className="cursor-pointer text-sm font-medium text-black dark:text-white">
                        Parâmetros desta regra (sobrescrevem os da empresa)
                        {Object.keys(form.params || {}).length
                            ? ` · ${Object.keys(form.params).length} definido(s)`
                            : ''}
                    </summary>
                    <div className="mt-3">
                        <ParamsEditor
                            value={form.params}
                            onChange={(params) => setField('params', params)}
                            mode="rule"
                        />
                    </div>
                </details>
                {error ? <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p> : null}
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button type="submit" loading={saving} disabled={saving || !form.name.trim()}>
                        Salvar regra
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
